import { ItemType } from '@mimir/common/dto/itemTypes';
import { createUuid } from '@mimir/common/util/createUuid';
import { exceptionSummary } from '@mimir/common/util/exception';
import { SafeHtmlBuilder } from '@mimir/common/util/safeHtmlBuilder';
import { addNotification, updateNotification, deleteNotification } from '../globals/notifications';
import { type ICollapsibleNotification, NotificationStackItemType } from '../model/notificationTypes';
import { getGlobalStore } from '../store';
import { goToFolder } from '../util/folder';
import type { BatchImportNotifier } from '@mimir/client-common/model/batchImportNotifier';
import { isInElectron, type ApplicationContext, isInPremiere, type AdobePanelContext, type ElectronContext } from '@mimir/common/context/applicationContext';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';

interface BatchImportDetails {
  /**
   * Increased as new files to download are processed
   */
  numOfFilesToDownload: number;

  /**
   * Increased as files have been downloaded
   */
  numOfFilesDownloaded: number;
}

export class ImportNotifier implements BatchImportNotifier {
  private readonly notification: ICollapsibleNotification;
  private batchImportDetails: BatchImportDetails;
  public calculating: boolean;
  public folderName_?: string;
  finalized = false;

  constructor(
    private applicationContext: ApplicationContext,
    title: string | SafeHtmlBuilder,
    description: string | SafeHtmlBuilder,
    folderId?: string,
  ) {
    this.notification = {
      title,
      description,
      type: NotificationStackItemType.Collapsible,
      key: folderId ?? createUuid(),
      loading: true,
      showDragIcon: false,
      itemType: ItemType.FOLDER,
      routeBtn: folderId ? { handler: () => goToFolder({ id: folderId }), tooltip: 'Open folder' } : undefined,
      isClosed: false,
      isComplete: false,
    };
    addNotification(this.notification);

    this.batchImportDetails = { numOfFilesToDownload: 0, numOfFilesDownloaded: 0 };
    this.calculating = true;
  }

  setCalculating(b: boolean) {
    this.calculating = b;
    if (!this.calculating && (this.numOfFilesDownloaded >= this.numOfFilesToDownload)) {
      // If we are done calculating the batch and also have no files to
      // download we know we are done
      this.markCompleted();
    }
  }

  folderName() {
    return this.folderName_;
  }

  get numOfFilesToDownload() {
    return this.batchImportDetails.numOfFilesToDownload;
  }

  get numOfFilesDownloaded() {
    return this.batchImportDetails.numOfFilesDownloaded;
  }

  private setTitle(title: string | SafeHtmlBuilder) {
    this.notification.title = title;
    updateNotification(this.notification);
  }

  private setIsComplete(isComplete: boolean) {
    this.notification.isComplete = isComplete;
    updateNotification(this.notification);
  }

  public setShowDragIcon(showDragIcon: boolean) {
    this.notification.showDragIcon = showDragIcon;
    updateNotification(this.notification);
  }

  public setDescription(description: string) {
    if (!this.finalized) {
      this.notification.description = description;
      updateNotification(this.notification);
    }
  }

  public setLoading(loading: boolean) {
    this.notification.loading = loading;
    updateNotification(this.notification);
  }

  public setPathToDrag(pathToDrag: string) {
    this.notification.pathToDrag = pathToDrag;
    updateNotification(this.notification);
  }

  public updateDownloadMessage() {
    if (!this.calculating) {
      if (this.numOfFilesToDownload > 0) {
        this.setTitle(`Downloaded ${this.numOfFilesDownloaded}/${this.numOfFilesToDownload} items`);
      } else {
        this.setDescription(isInElectron(this.applicationContext) ? 'Download complete.' : 'Importing items...');
      }
    }
  }

  public setFolderName(folderName: string) {
    this.folderName_ = folderName;
  }

  public incrementNumOfFilesToDownload() {
    this.batchImportDetails.numOfFilesToDownload += 1;
  }

  public incrementNumOfFilesDownloaded() {
    this.batchImportDetails.numOfFilesDownloaded += 1;
    this.updateDownloadMessage();
    if (!this.calculating && (this.numOfFilesDownloaded >= this.numOfFilesToDownload)) {
      // If we are done calculating the batch and also have no files to
      // download we know we are done
      this.markCompleted();
    }
  }

  public markCompleted() {
    const folderName = this.folderName();
    if (folderName) {
      this.setIsComplete(true);
      const safeHtmlBuilder = new SafeHtmlBuilder('Downloaded folder: ').addBoldText(folderName);
      this.setTitle(safeHtmlBuilder);
      if (!this.notification.loading && getGlobalStore().getters.folderDragEnabled) {
        this.setShowDragIcon(true);
      }
    } else {
      this.remove();
    }
  }

  public markFailed(cause: unknown) {
    this.setIsComplete(true);
    this.setLoading(false);
    this.setTitle('Import failed');
    this.setDescription(exceptionSummary(cause));
    this.finalized = true;
  }

  public remove() {
    deleteNotification(this.notification);
  }
}


export function getNotifier(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    notifier: BatchImportNotifier | undefined,
  },
  projectsOnly?: boolean,
): BatchImportNotifier | undefined {
  if (isInPremiere(services.applicationContext) && projectsOnly) {
    // import notification doesn't make sense when opening project files
    services.notifier?.remove();
    services.notifier = undefined;

    return undefined;
  }

  const calculatingMsg = `Checking contents and ${isInElectron(services.applicationContext) ? 'skipping download' : 'importing' } if available`;

  if (services.notifier) {
    services.notifier.setDescription(calculatingMsg);
    return services.notifier;
  }

  return services.notificationOps.createNotifier(isInElectron(services.applicationContext) ? 'Downloading items' : 'Importing items to premiere', calculatingMsg, undefined);
}
